import { Button } from "@app/ui";
import { useEffect, useState } from "react";
import { useSettingsStore } from "../../stores/index.js";
import type { ShortcutAction } from "../../stores/settings.store.js";
import type { I18nKey } from "../../lib/language.js";
import { chordFromEvent, formatChord } from "../../lib/shortcut-chord.js";
import { useI18n } from "../../lib/useI18n.js";
import { SettingsCard } from "./SettingsCard.js";

const LABELS: Array<[action: ShortcutAction, labelKey: I18nKey]> = [
  ["newTask", "shortcuts.newTask"],
  ["today", "shortcuts.today"],
  ["priorities", "shortcuts.priorities"],
  ["calNext", "shortcuts.calNext"],
  ["calPrev", "shortcuts.calPrev"],
  ["help", "shortcuts.help"],
  ["undo", "shortcuts.undo"],
];

const MODIFIERS = new Set(["Shift", "Control", "Alt", "Meta"]);

/** Rebindable keyboard-shortcut list. Desktop-only — callers gate it on
 *  `!useMobileUx()` (a phone has no keyboard chords). */
export function ShortcutsCard() {
  const { t } = useI18n();
  const shortcuts = useSettingsStore((s) => s.shortcuts);
  const setShortcut = useSettingsStore((s) => s.setShortcut);
  const resetShortcuts = useSettingsStore((s) => s.resetShortcuts);
  const [listening, setListening] = useState<ShortcutAction | null>(null);

  useEffect(() => {
    if (!listening) return;
    // Capture phase + stopImmediatePropagation so the rebind keypress doesn't
    // also trigger the global shortcut handler (which listens in the bubble phase).
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (e.key === "Escape") { setListening(null); return; }
      if (MODIFIERS.has(e.key)) return; // wait for the non-modifier key in the combo
      void setShortcut(listening, chordFromEvent(e));
      setListening(null);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [listening, setShortcut]);

  const dupes = duplicateActions(shortcuts);

  return (
    <SettingsCard
      id="keyboard-shortcuts"
      title={t("shortcuts.title")}
      description={t("shortcuts.description")}
      onReset={() => void resetShortcuts()}
    >
      <ul className="flex flex-col divide-y divide-border">
        {LABELS.map(([action, labelKey]) => {
          const desc = t(labelKey);
          return (
            <li key={action} className="flex items-center justify-between gap-4 py-2 text-sm">
              <span>{desc}</span>
              <button
                type="button"
                onClick={() => setListening(action)}
                aria-label={t("shortcuts.rebind", { desc })}
                className={`min-w-[5rem] rounded border px-3 py-1 text-center font-mono text-xs ${
                  listening === action ? "border-primary text-primary" : "hover:bg-accent"
                } ${dupes.has(action) ? "border-destructive text-destructive" : ""}`}
              >
                {listening === action ? t("shortcuts.pressKey") : formatChord(shortcuts[action])}
              </button>
            </li>
          );
        })}
      </ul>
      {dupes.size > 0 && (
        <p className="mt-2 text-xs text-destructive">{t("shortcuts.duplicate")}</p>
      )}
    </SettingsCard>
  );
}

function duplicateActions(map: Record<ShortcutAction, string>): Set<ShortcutAction> {
  const counts = new Map<string, ShortcutAction[]>();
  for (const a of Object.keys(map) as ShortcutAction[]) {
    (counts.get(map[a]) ?? counts.set(map[a], []).get(map[a])!).push(a);
  }
  const out = new Set<ShortcutAction>();
  for (const actions of counts.values()) if (actions.length > 1) actions.forEach((a) => out.add(a));
  return out;
}
